import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import React, { useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks"
import { toggleVobRequestNoteAddModal } from "../../../../../app/slices/VerificationModuleSlice";
import { VobRequestNotePostCommand, VerificationSaveCommand } from "../../../../../services/verifyService/commands";
import { Note } from "../../../../../services/verifyService/model/verification/Note";
import { addNote } from "../../../../../services/verifyService/model/verification/VerificationSlice";
import { useSaveVobRequestNoteMutation, useSaveVerificationMutation } from "../../../../../services/verifyService/verify-api";

const VOB_REQUEST_REASONS = [
    'Need posting sheet',
    'Need cpt code updated',
    'Need member ID Card',
    'Missing policy #',
    'Need stay type changed',
    'EPR Updated',
    'No reimbursement',
    'DOB Incorrect',
    'Need Secondary Insurance',
    'Other'
] as const;

function VobRequestNoteAdd(){
    const dispatch = useAppDispatch();
    const modalOpen = useAppSelector((state) => state.verificationModule.VobRequestNoteAddModal);
    const verification = useAppSelector((state) => state.verification.verification);
    const [vobRequestReason,setVobRequestReason] = useState('');
    const [otherReason, setOtherReason] = useState('');
    const [saveVobRequestNoteToServer] = useSaveVobRequestNoteMutation();
    const [saveVerification] = useSaveVerificationMutation();
    const [isSaving, setIsSaving] = useState(false);

    const handleReasonChange = (event: SelectChangeEvent<string>) => {
        setVobRequestReason(event.target.value);
        if (event.target.value !== 'Other') {
            setOtherReason('');
        }
    };

    const getEffectiveReason = () => {
        return vobRequestReason === 'Other' ? otherReason : vobRequestReason;
    };
    
    const saveNote = async () => {
        setIsSaving(true);
        try {
            const effectiveReason = getEffectiveReason();
            let newNote = new Note();
            newNote.noteText = `VOB REQUEST - Reason: ${effectiveReason}`; 
            newNote.changeBy='You'; 
            newNote.changeDate = new Date();
            dispatch(addNote(newNote));
            // First save the VOB request note event, then save verification to avoid race condition
            await saveVobRequestNoteToServer(new VobRequestNotePostCommand(verification.id, effectiveReason)).unwrap();
            // Save the verification to persist the isVobRequest toggle state
            await saveVerification(new VerificationSaveCommand(verification)).unwrap();
            setVobRequestReason('');
            setOtherReason('');
            dispatch(toggleVobRequestNoteAddModal());
        } catch (error) {
            console.error('Error saving VOB request:', error);
            // TODO: Add user-facing error notification (e.g., toast/snackbar)
            alert('Failed to save VOB request. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }

    const handleClose = () => {
        if (isSaving) return; // Prevent closing during save
        setVobRequestReason('');
        setOtherReason('');
        dispatch(toggleVobRequestNoteAddModal());
    }

    const isFormValid = () => {
        return vobRequestReason === 'Other' 
            ? otherReason.trim() !== '' 
            : vobRequestReason !== '';
    };

    return <React.Fragment>
        <Dialog 
            open={modalOpen} 
            onClose={isSaving ? undefined : handleClose} 
            maxWidth="md" 
            fullWidth
            disableEscapeKeyDown={isSaving}
        >
            <DialogTitle>VOB Request</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{marginBottom:'20px'}}>
                    <AlertTitle>Instructions</AlertTitle>
                    Please select a reason for this VOB request.
                </Alert>
                <FormControl fullWidth sx={{marginTop:'10px', marginBottom:'20px'}}>
                    <InputLabel id="vob-request-reason-label" shrink>Reason for Request *</InputLabel>
                    <Select
                        labelId="vob-request-reason-label"
                        value={vobRequestReason}
                        onChange={handleReasonChange}
                        label="Reason for Request *"
                        displayEmpty
                        notched
                    >
                        <MenuItem value="" disabled>
                            <em>Select a reason...</em>
                        </MenuItem>
                        {VOB_REQUEST_REASONS.map((reason) => (
                            <MenuItem key={reason} value={reason}>
                                {reason}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {vobRequestReason === 'Other' && (
                    <TextField 
                        label='Please specify reason' 
                        value={otherReason} 
                        onChange={(e)=>setOtherReason(e.target.value)} 
                        required
                        fullWidth
                        InputLabelProps={{shrink:true}} 
                        placeholder="Enter your reason..."
                        inputProps={{ maxLength: 500 }}
                        helperText={`${otherReason.length}/500 characters`}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
                <Button 
                    onClick={saveNote}
                    disabled={!isFormValid() || isSaving}
                    variant="contained"
                >
                    {isSaving ? 'Saving...' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}

export {VobRequestNoteAdd}
